import { Box, Button, CircularProgress, Grid, Tooltip, Typography } from "@mui/material";

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";

import React, { useContext, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";

import {
  PurchaseOrder,
  PurchaseOrderLineItem,
  PurchaseOrderState,
  ProductVariant
} from "../types/api";

import { useLineItems } from "../hooks/useLineItems";
import { LineItemStatus, LineItemVariant } from "../types/lineItemTypes";

import PurchaseOrderLineItemTable from "../components/po-lineitem-table.component";
import { CreatePOLineItemModal } from "../components/modal.create-polineitem";
import {
  createPurchaseOrder,
  updatePurchaseOrder,
} from "../services/purchasing.service";
import { PurchaseOrdersRoute, ViewPurchaseOrderRoute } from "../routes";
import GlobalMessageContext from "../context/globalMessage.context";
import { MISSING_PRODUCT_IMAGE_URL, VAT_RATE } from "../config";

import _ from "lodash";
import { CreatePOSPELineItemModal } from "../components/modal.create-spelineitem";
import { POSummary } from "../components/po-summary.component";
import { SupplierPicker } from "../components/supplier-picker.component";

const EditPurchaseOrderView = (props: any) => {
  const uxMessage = useContext(GlobalMessageContext);
  const navigate = useNavigate();
  const addProductButton = useRef(null);

  // If we are passed a PO, then we are editing that rather than creating a new one
  const { state } = useLocation();
  const po = state ? state["po"] : undefined;
  const title = po ? `Update ${po.number}` : "Create Purchase Order";

  // const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [newPo, setNewPo] = useState<PurchaseOrder>(
    po
      ? { ...po }
      : {
          id: undefined,
          state: PurchaseOrderState.DRAFT,
          lineItems: [],
          issueDate: new Date(),
        }
  );

  const [showLineItemDialog, setShowLineItemDialog] = useState(false);
  const [showSPEDialog, setShowSPEDialog] = useState(false);
  const [highlightedTempId, setHighlightedTempId] = useState<string | null>(null);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitialMountRef = useRef(true);
  const performAutosaveRef = useRef<() => void>(() => {});

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    trigger,
  } = useForm();

  // Initialize line items hook
  const {
    items: lineItems,
    summary,
    addItem,
    removeItem,
    updateQuantity,
    updateField,
    moveItemUp,
    moveItemDown,
    getCleanItems,
    mergeServerItems
  } = useLineItems(newPo.lineItems || []);

  // Update PO totals whenever line items change
  React.useEffect(() => {
    setNewPo(prev => ({
      ...prev,
      subTotal: summary.subTotal,
      taxAmount: summary.taxAmount,
      total: summary.total
    }));
  }, [summary]);

  // Always keep the ref pointing at the latest closure so the debounce timer
  // never calls a stale version after React re-renders update newPo / summary.
  performAutosaveRef.current = () => {
    if (isAutoSaving) return;
    setIsAutoSaving(true);
    const cleanLineItems = getCleanItems();
    const poToSave = {
      ...newPo,
      lineItems: cleanLineItems,
      subTotal: summary.subTotal,
      taxAmount: summary.taxAmount,
      total: summary.total,
    };

    if (!newPo.guid) {
      createPurchaseOrder({
        po: poToSave,
        success: (r) => {
          setNewPo(prev => ({ ...prev, guid: r.data.guid, number: r.data.number, id: r.data.id }));
          // Reconcile server-assigned line item IDs back into local state so the next
          // autosave updates these rows instead of re-creating them (prevents duplicates).
          mergeServerItems(r.data.lineItems || []);
          setIsAutoSaving(false);
        },
        failure: () => setIsAutoSaving(false),
      });
    } else {
      updatePurchaseOrder({
        po: poToSave,
        success: (r) => {
          // Newly-created items (added since the last save) come back with real IDs here;
          // merge them in so subsequent autosaves don't duplicate them.
          mergeServerItems(r.data.lineItems || []);
          setIsAutoSaving(false);
        },
        failure: () => setIsAutoSaving(false),
      });
    }
  };

  // Autosave: debounce 2s after any change, only when at least one line item exists
  React.useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }
    if (summary.itemCount === 0) return;
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => performAutosaveRef.current(), 2000);
    return () => { if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current); };
  }, [lineItems, newPo.supplier?.guid, newPo.issueDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const supplierChanged = (event: any, supplier: any) => {
    setNewPo({ ...newPo, supplier: supplier ? { guid: supplier.guid, name: supplier.name } : null });
    // Update form validation
    setValue("supplier", supplier ? supplier.guid : "");
    trigger("supplier");
  };

  const issueDateChanged = (date: any) => {
    setNewPo({ ...newPo, issueDate: date });
  };

  const save = () => {
    // This prevents form submission when CR is used in the line item modals
    if (showLineItemDialog || showSPEDialog) return;

    if (summary.itemCount === 0) {
      uxMessage.setMessage({
        message: `Please create at least one line item`,
        severity: "warning",
      });
      return;
    }

    // Cancel any pending autosave to avoid a race condition
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);

    // Get clean line items from hook (removes temp fields and IDs)
    const cleanLineItems = getCleanItems();

    // newPo.guid is set if autosave already created this PO
    const isExistingPo = po || newPo.guid;
    const createOrUpdate = isExistingPo ? updatePurchaseOrder : createPurchaseOrder;

    createOrUpdate({
      po: {
        ...newPo,
        lineItems: cleanLineItems,
        subTotal: summary.subTotal,
        taxAmount: summary.taxAmount,
        total: summary.total
      },
      success: (r) => {
        uxMessage.setMessage({
          message: `${r.data.number} successfully ${
            isExistingPo ? "updated" : "created"
          }`,
          severity: "success",
        });

        // View the PO just created/updated
        navigate(ViewPurchaseOrderRoute, {
          state: { selectedPo: r.data.number },
        });
      },
      failure: (r) => {
        uxMessage.setMessage({
          message: `Sorry - something went wrong`,
          severity: "error",
        });
      },
    });
  };

  const deleteLineItem = (tempId: string) => {
    // Prevent deletion of the last item
    if (summary.itemCount === 1) {
      uxMessage.setMessage({
        message: "Sorry - you cannot delete the last item",
        severity: "error",
      });
      return;
    }

    try {
      removeItem(tempId);
    } catch (error) {
      uxMessage.setMessage({
        message: `Error deleting item: ${error.message}`,
        severity: "error",
      });
    }
  };

  const addLineItem = (v: ProductVariant, quantity: number) => {
    if (!v) return;

    try {
      addItem(v, quantity);
      // Only scroll to the Add button if the user is near the top of the page
      // This prevents unwanted scrolling when working on long purchase orders
      if (window.scrollY < 200) {
        addProductButton.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    } catch (error) {
      uxMessage.setMessage({
        message: `Error adding item: ${error.message}`,
        severity: "error",
      });
      const existing = lineItems.find(
        (item) => item.variantSku === v.sku && item.status === LineItemStatus.ACTIVE
      );
      if (existing) setHighlightedTempId(existing.tempId);
    }
  };

  return (
    <>
      <Typography variant="h1">{title}</Typography>
      <form onSubmit={handleSubmit((d) => save())}>
        <Box sx={{ m: 1 }}>
          <Grid container spacing={2}>
            <Grid size={10}>
              <SupplierPicker
                register={register}
                errors={errors}
                value={newPo.supplier?.guid}
                supplierChanged={supplierChanged}
                setValue={setValue}
                trigger={trigger}
              />
            </Grid>
            <Grid size={2}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Issue Date"
                  value={new Date(newPo.issueDate)}
                  onChange={(value) => issueDateChanged(value)}
                  format="dd-MM-yyyy"
                />
              </LocalizationProvider>
            </Grid>
            {summary.itemCount > 0 && (
              <Grid size={4}>
                <POSummary po={{
                  ...newPo,
                  subTotal: summary.subTotal,
                  taxAmount: summary.taxAmount,
                  total: summary.total
                }} hideDate />
              </Grid>
            )}
            <Grid size={12}>
              <Typography variant="h2">Line Items</Typography>
              <PurchaseOrderLineItemTable
                items={lineItems}
                parentPo={newPo}
                onUpdateQuantity={updateQuantity}
                onUpdateField={updateField}
                onDeleteRow={deleteLineItem}
                onMoveItemUp={moveItemUp}
                onMoveItemDown={moveItemDown}
                allowEditQuantity={true}
                allowReordering={newPo.state === PurchaseOrderState.DRAFT}
                hideReceiveCols={true}
                highlightedTempId={highlightedTempId}
                onClearHighlight={() => setHighlightedTempId(null)}
              />
              <Tooltip title={!newPo.supplier ? "Please select a supplier before adding products" : ""}>
                <span>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => setShowLineItemDialog(true)}
                    sx={{ margin: 1 }}
                    ref={addProductButton}
                    disabled={!newPo.supplier}
                  >
                    Add Product
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={!newPo.supplier ? "Please select a supplier before adding products" : ""}>
                <span>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => setShowSPEDialog(true)}
                    sx={{ margin: 1 }}
                    disabled={!newPo.supplier}
                  >
                    Add Special Order
                  </Button>
                </span>
              </Tooltip>
              <CreatePOLineItemModal
                open={showLineItemDialog}
                onClose={() => setShowLineItemDialog(false)}
                onSelectVariant={(v: ProductVariant, quantity: number) => {
                  addLineItem(v, quantity);
                  setShowLineItemDialog(false);
                }}
              />
              <CreatePOSPELineItemModal
                open={showSPEDialog}
                onClose={() => setShowSPEDialog(false)}
                onSave={(spe: LineItemVariant, quantity: number) => {
                  addLineItem(spe, quantity);
                  setShowSPEDialog(false);
                }}
              />
            </Grid>
            <Grid size={12}>
              <Box
                sx={{
                  position: 'sticky',
                  bottom: 0,
                  p: 2,
                  mt: 3,
                  display: 'flex',
                  justifyContent: 'space-between'
                }}
              >
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    navigate(PurchaseOrdersRoute);
                  }}
                >
                  Cancel
                </Button>
                {isAutoSaving && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                    <CircularProgress size={14} color="inherit" />
                    <Typography variant="caption">Saving…</Typography>
                  </Box>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={showLineItemDialog}
                >
                  {!po
                    ? "Save as Draft"
                    : po.state === PurchaseOrderState.DRAFT
                    ? "Update Draft"
                    : "Update"}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </form>
    </>
  );
};

export default EditPurchaseOrderView;
